import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/atoms/form";
import { ControlledCombobox } from "~/molecules/ControlledCombobox";
import { reviewPeriod } from "~/config/types";
import { getReviewPeriodLabel } from "~/helpers/document";
import { Button } from "~/atoms/button";
import { Trash } from "lucide-react";
import { type DocumentEditFormValues } from "~/hooks/useDocumentEditForm";
import { Alert, AlertDescription } from "~/atoms/alert";
import { Link, useLocation, useSearchParams } from "react-router";
import { TabId } from "~/molecules/DocumentPanelTabsState";

type ScheduledReviewFormFieldValues = Pick<
  DocumentEditFormValues,
  "scheduleReviewPeriod"
>;

export const DocumentScheduledReviewFields = () => {
  const { t } = useTranslation();
  const form = useFormContext<ScheduledReviewFormFieldValues>();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const workflowTabSearchParams = new URLSearchParams(searchParams);
  workflowTabSearchParams.set("tab", TabId.workflow);
  const workflowTabHref = `${location.pathname}?${workflowTabSearchParams.toString()}`;

  return (
    <div className="flex flex-col gap-3">
      <Alert variant="info">
        <AlertDescription>
          {t(($) => $.commons.document.reviewDatesMovedToWorkflow)}{" "}
          <Link className="font-medium underline" to={workflowTabHref}>
            {t(($) => $.commons.document.goToWorkflowTab)}
          </Link>
        </AlertDescription>
      </Alert>
      <FormField
        control={form.control}
        name="scheduleReviewPeriod"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t(($) => $.commons.document.recurringPeriod)}
            </FormLabel>
            <FormControl>
              <div className="flex gap-2">
                <ControlledCombobox
                  value={field.value ?? undefined}
                  onChange={field.onChange}
                  placeholder={t(($) => $.commons.customSelect.selectOption)}
                  options={reviewPeriod.map((value) => ({
                    value,
                    label: getReviewPeriodLabel(value, t) ?? value,
                  }))}
                />
                {field.value && (
                  <Button
                    onClick={() => field.onChange(null)}
                    variant="ghost"
                    size="icon"
                    type="button"
                  >
                    <Trash />
                  </Button>
                )}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
